import { MixerCard } from '@/components/MixerCard';
import { QuestionType } from '@/contracts/enums/questionTypes';
import { EditorQuestion } from '@/contracts/models/survey-editor';
import { Card } from 'primereact/card';
import { useState } from 'react';
import { VoteButton } from '@/components/VoteButton';
import { MetricCard } from '../../reporting/components/MetricCard';
import { QuestionCard } from '../../survey-builder/components/questions/QuestionCard';

export const CustomComponentsSection = () => {
  const [vote1, setVote1] = useState(false);
  const [vote2, setVote2] = useState(true);
  const [vote3, setVote3] = useState(false);

  // Example questions for QuestionCard
  const multipleChoiceQuestion: EditorQuestion = {
    id: 'q1',
    type: QuestionType.RadioGroup,
    name: 'feedback',
    title: 'What is your feedback on this feature?',
    isRequired: false,
    choices: ['Excellent', 'Good', 'Fair', 'Poor'].map(text => ({
      value: text,
      text,
    })),
  };

  const ratingQuestion: EditorQuestion = {
    id: 'q2',
    type: QuestionType.Rating,
    name: 'experience',
    title: 'How would you rate your experience?',
    isRequired: false,
    rateMin: 1,
    rateMax: 5,
  };

  return (
    <div id='custom-components' className='space-y-6'>
      <div>
        <h2 className='text-2xl font-bold mb-2'>Custom Components</h2>
        <p className='text-gray-600 mb-4'>
          Application-specific custom components built for unique functionality.
        </p>
      </div>

      {/* VoteButton */}
      <Card title='VoteButton' className='mb-4'>
        <div className='space-y-4'>
          <p className='text-sm text-gray-600'>
            Custom voting button component used in community mixer features for
            upvoting answers.
          </p>

          <div>
            <h3 className='text-sm font-semibold mb-3 text-gray-700'>
              Vote Button States
            </h3>
            <div className='flex flex-wrap items-center gap-6'>
              <div className='flex flex-col items-center gap-2'>
                <VoteButton hasVoted={vote1} onClick={() => setVote1(!vote1)} />
                <span className='text-xs text-gray-500'>Not Voted</span>
              </div>

              <div className='flex flex-col items-center gap-2'>
                <VoteButton hasVoted={vote2} onClick={() => setVote2(!vote2)} />
                <span className='text-xs text-gray-500'>Voted</span>
              </div>

              <div className='flex flex-col items-center gap-2'>
                <VoteButton hasVoted={vote3} onClick={() => setVote3(!vote3)} />
                <span className='text-xs text-gray-500'>
                  Interactive Example
                </span>
              </div>

              <div className='flex flex-col items-center gap-2'>
                <VoteButton hasVoted={false} onClick={() => {}} disabled />
                <span className='text-xs text-gray-500'>Disabled</span>
              </div>
            </div>
          </div>

          <div className='bg-gray-50 p-4 rounded border'>
            <h3 className='text-sm font-semibold mb-2'>Features</h3>
            <ul className='text-sm text-gray-600 space-y-1 list-disc list-inside'>
              <li>Toggle vote state with single click</li>
              <li>Displays &quot;Vote&quot; label with thumbs-up icon</li>
              <li>Visual feedback for voted state (filled background)</li>
              <li>Blue color scheme matching brand</li>
              <li>Disabled state support</li>
            </ul>
          </div>
        </div>
      </Card>

      {/* MixerCard */}
      <Card title='MixerCard' className='mb-4'>
        <div className='space-y-4'>
          <p className='text-sm text-gray-600'>
            Card component for displaying community mixer answers with voting
            functionality and text expansion.
          </p>

          <div className='border rounded-lg overflow-hidden'>
            <MixerCard
              answer={{
                id: '1',
                answerValue:
                  'This is an example community mixer answer. It demonstrates how answers are displayed with user avatars and voting capabilities.',
              }}
              displayName='John Doe'
              hasVoted={false}
              voteCount={3}
              isUserAnswer={false}
              onVote={() => {}}
              isVotingEnabled={true}
            />

            <MixerCard
              answer={{
                id: '2',
                answerValue:
                  'Another example answer with more votes. The component supports text truncation for long answers with a "show more" toggle for better readability. This text is intentionally long to demonstrate the truncation feature at 150 characters, which allows users to expand and collapse lengthy responses for a cleaner interface.',
              }}
              displayName='Alice Smith'
              hasVoted={true}
              voteCount={8}
              isUserAnswer={false}
              onVote={() => {}}
              isVotingEnabled={true}
            />

            <MixerCard
              answer={{
                id: '3',
                answerValue:
                  'Example with voting disabled to show how the component adapts when voting is not enabled.',
              }}
              displayName='Bob Johnson'
              hasVoted={false}
              voteCount={0}
              isUserAnswer={false}
              onVote={() => {}}
              isVotingEnabled={false}
            />
          </div>

          <div className='bg-gray-50 p-4 rounded border'>
            <h3 className='text-sm font-semibold mb-2'>Features</h3>
            <ul className='text-sm text-gray-600 space-y-1 list-disc list-inside'>
              <li>User avatar display (initials or image)</li>
              <li>Answer text with automatic truncation at 150 characters</li>
              <li>Expand/collapse for long text</li>
              <li>Integrated voting with VoteButton</li>
              <li>Vote count display</li>
              <li>Memoized for performance optimization</li>
              <li>Optional voting - can be disabled per card</li>
            </ul>
          </div>
        </div>
      </Card>

      {/* QuestionCard */}
      <Card title='QuestionCard' className='mb-4'>
        <div className='space-y-4'>
          <p className='text-sm text-gray-600'>
            Card component for displaying and editing survey questions in the
            survey builder.
          </p>

          <div className='space-y-4'>
            <QuestionCard
              question={multipleChoiceQuestion}
              index={0}
              onUpdate={() => {}}
              onDelete={() => {}}
              onDuplicate={() => {}}
              onMoveUp={() => {}}
              onMoveDown={() => {}}
            />

            <QuestionCard
              question={ratingQuestion}
              index={1}
              onUpdate={() => {}}
              onDelete={() => {}}
              onDuplicate={() => {}}
              onMoveUp={() => {}}
              onMoveDown={() => {}}
            />
          </div>

          <div className='bg-gray-50 p-4 rounded border'>
            <h3 className='text-sm font-semibold mb-2'>Features</h3>
            <ul className='text-sm text-gray-600 space-y-1 list-disc list-inside'>
              <li>Numbered questions (1., 2., etc.)</li>
              <li>Inline title editing with InputText</li>
              <li>Reorder buttons (move up/down with chevron icons)</li>
              <li>Question type indicator</li>
              <li>Border styling with rounded corners</li>
              <li>
                Displays question options (multiple choice, rating scale, etc.)
              </li>
            </ul>
          </div>
        </div>
      </Card>

      {/* MetricCard */}
      <Card title='MetricCard' className='mb-4'>
        <div className='space-y-4'>
          <p className='text-sm text-gray-600'>
            Card component for displaying key metrics and statistics in reports
            and dashboards.
          </p>

          <div>
            <h3 className='text-sm font-semibold mb-3 text-gray-700'>
              Vertical Layout
            </h3>
            <div className='grid grid-cols-2 md:grid-cols-4 gap-4'>
              <MetricCard
                icon='pi-users'
                value='1,234'
                label='Participants'
                variant='participants'
                layout='vertical'
              />
              <MetricCard
                icon='pi-comment'
                value='5,678'
                label='Responses'
                variant='responses'
                layout='vertical'
              />
              <MetricCard
                icon='pi-heart'
                value='892'
                label='Likes'
                variant='likes'
                layout='vertical'
              />
              <MetricCard
                icon='pi-clock'
                value='3m 42s'
                label='Avg. Time'
                variant='time'
                layout='vertical'
              />
            </div>
          </div>

          <div>
            <h3 className='text-sm font-semibold mb-3 text-gray-700'>
              Horizontal Layout with Delta
            </h3>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
              <MetricCard
                icon='pi-chart-line'
                value='1,234'
                label='Total Views'
                variant='default'
                layout='horizontal'
                delta={{ value: 12, label: '%', isPositive: true }}
              />
              <MetricCard
                icon='pi-users'
                value='456'
                label='Completion Rate'
                variant='completion'
                layout='horizontal'
                delta={{ value: 5, label: '%', isPositive: false }}
              />
            </div>
          </div>

          <div className='bg-gray-50 p-4 rounded border'>
            <h3 className='text-sm font-semibold mb-2'>Features</h3>
            <ul className='text-sm text-gray-600 space-y-1 list-disc list-inside'>
              <li>Two layout variants: vertical and horizontal</li>
              <li>Icon, value, and label display</li>
              <li>Optional delta indicator (positive/negative)</li>
              <li>Multiple color variants for different metric types</li>
              <li>Responsive design</li>
            </ul>
          </div>
        </div>
      </Card>
    </div>
  );
};
